// The site's four places (docs/refactor-plan.md): Explore, Make, Play and You. The menu shows the
// places; each place shows its tabs across the top of its pages. Every page belongs to one place,
// which the menu highlights. Plain data and functions, so tests read them too. A site changes the
// places and tabs with the "nav.places" filter (src/platform/core.js), so the functions here that read
// them take `places`.

import { toolNamed } from "../../lib/tools/registry";
import { EDITIONS, editionOf, enabledExtensions, siteFlags, tabsOf } from "../../lib/extensions/registry";

// The feature flags (src/lib/extensions/registry.js).
export const FLAGS = siteFlags();

// The four places never change. Their tabs come from the packages in src/lib/extensions/registry.js,
// and show unless they say otherwise: their package must be on (one that's off hides a tab, never a
// place), `who` is "signedOut", "signedIn" or "admin", `books` waits until this browser has opened
// a book's practice page, `roles` asks for a role in the program that's open, and `app` says which
// app it belongs to (or see TAB_APPS).
const FOUR = [{ id: "explore", label: "Explore", href: "/community" }, { id: "make", label: "Make", href: "/" }, { id: "play", label: "Play", href: "/practice" }, { id: "you", label: "You", href: "/profile" }];
export function placesFor(flags = FLAGS) {
  const name = editionOf(flags), tabs = tabsOf(name);
  return FOUR.map((place) => ({ ...place, ...EDITIONS[name].places?.[place.id], tabs: tabs.filter((tab) => tab.place === place.id).map(({ place: _, ...tab }) => tab) }));
}
export const PLACES = placesFor();

// Which place each page is in, by its route as Next names it. Pages left out (the welcome page, and
// pages shown inside other apps) have no menu.
const PAGES = {
  explore: ["/community", "/reels", "/graph", "/world", "/tune", "/discover", "/search", "/feed", "/rhythms/[id]", "/shared/[rid]", "/i/[...ref]", "/store", "/store/[id]"],
  make: ["/", "/daw", "/synth", "/room", "/grow", "/studio", "/new/recording", "/r/[rid]", "/daw/plugin", "/plugin", "/link", "/tools"],
  play: ["/practice", "/sessions/[id]", "/vault", "/exercises", "/rudiments", "/byos", "/book", "/book/page/[pageNumber]", "/q/[token]", "/account/books", "/store/mine", "/read/[id]", "/school/[[...path]]", "/lessons/[[...path]]"],
  you: ["/profile", "/library", "/u/[handle]", "/admin/moderation", "/modules", "/modules/[id]", "/modules/new", "/book-builder", "/notifications",
    "/login", "/signup", "/emailConfirmation", "/oathRedirect", "/mine", "/mods", "/telegram/link", "/extensions", "/extensions/[id]", "/extensions/new", "/extensions/[id]/edit",
    "/store/sell", "/store/sell/[id]", "/admin/review", "/admin/site", "/examples/[id]",
    "/terms", "/privacy", "/guidelines", "/platform", "/copyright", "/copyright/notice", "/copyright/counter-notice"],
};
const PLACE_OF = new Map(Object.entries(PAGES).flatMap(([place, routes]) => routes.map((route) => [route, place])));
// The band's pages (src/lib/marching) are where its tabs are: Band in Play on the full site, its own
// place in the marching edition, with the staff's pages in You.
const STAFF_PAGES = /^\/band\/\[slug\]\/(members|settings|moderation|packets)/;
const placeWithTab = (id, places) => places.find((place) => place.tabs.some((tab) => tab.id === id))?.id;
const bandPlace = (pathname, places) => (STAFF_PAGES.test(pathname) && placeWithTab("band-members", places)) || placeWithTab("band", places) || "play";
// A tool's own page (/tools/<id>) is in the place its manifest names.
const pluginTab = (tab, query) => [`/${query.path?.[0]}`, `/x/${query.path?.[0]}`].includes(tab.href);
// A plugin's own page (/x/<plugin>/…) is in the place that has a tab for it. School and Lessons are
// in Play for someone who hasn't added them, too.
const PLUGIN_PLACES = { school: "play", lessons: "play" };
export function placeOf(pathname, query = {}, places = PLACES) {
  if (pathname === "/tools/[id]") return toolNamed(query.id)?.place || "make";
  if (pathname.startsWith("/band")) return /^\/band(\/|$)/.test(pathname) ? bandPlace(pathname, places) : null;
  if (pathname === "/x/[...path]") return places.find((place) => place.tabs.some((tab) => pluginTab(tab, query)))?.id || PLUGIN_PLACES[query.path?.[0]] || "you";
  return PLACE_OF.get(pathname) || null;
}
export const placeNamed = (id, places = PLACES) => places.find((place) => place.id === id) || null;

// The app each tab belongs to (src/platform/core.js): a site that turns an app off loses its tab.
export const TAB_APPS = { band: "marching", "band-feed": "marching", "band-drill": "marching", "band-music": "marching", "band-assignments": "marching", "band-members": "marching", "band-packets": "marching", feed: "community", people: "community", reels: "reels", map: "map", score: "score", song: "daw", synth: "daw", room: "room", practice: "practice", library: "vault",
  byos: "byos", books: "book", modules: "modules", store: "store", shelf: "store", extensions: "extensions" };
// The tabs a visitor sees in a place (`appEnabled` says which apps the site has).
export const shownTo = (who, user) => !who || (who === "signedOut" ? !user : who === "signedIn" ? Boolean(user) : Boolean(user?.isAdmin));
// `roles` are the visitor's in the program that's open (`program`, its slug), which ":slug" in an address stands for.
export function visibleTabs(id, { user = null, books = false, flags = FLAGS, appEnabled = () => true, places = placesFor(flags), roles = [], program = null } = {}) {
  const on = new Set(enabledExtensions(flags).map((item) => item.id));
  return (placeNamed(id, places)?.tabs || []).filter((tab) => (!tab.extension || on.has(tab.extension)) && (!tab.flag || flags[tab.flag] === "1") && shownTo(tab.who, user)
    && (!tab.books || books) && (!tab.roles || tab.roles.some((role) => roles.includes(role))) && (!(tab.app || TAB_APPS[tab.id]) || appEnabled(tab.app || TAB_APPS[tab.id])))
    .map((tab) => tab.href.includes(":slug") ? { ...tab, href: program ? tab.href.replace(":slug", program) : "/band" } : tab);
}
// Where a place goes from the menu: its own address while the site has that, otherwise its first tab
// the site has (a place whose apps are all off has none).
export function placeHref(place, appEnabled = () => true) {
  const appOf = (tab) => tab.app || TAB_APPS[tab.id], open = (tab) => !appOf(tab) || appEnabled(appOf(tab));
  const home = place.tabs.find((tab) => tab.href === place.href);
  if (home ? open(home) : place.tabs.every(open)) return place.href;
  return place.tabs.find((tab) => appOf(tab) && appEnabled(appOf(tab)))?.href || (place.id === "you" ? place.href : null);
}

// The tab for the page that's open, if it has one.
export function currentTab(pathname, query = {}, places = PLACES) {
  if (pathname.startsWith("/band/[slug]/")) {
    const page = pathname.split("/")[3], id = `band-${{ settings: "members", moderation: "feed" }[page] || page}`;
    return placeWithTab(id, places) ? id : "band";
  }
  switch (pathname) {
    case "/band": case "/band/join": case "/band/[slug]": return "band";
    case "/x/[...path]": return places.flatMap((place) => place.tabs).find((tab) => pluginTab(tab, query))?.id || null;
    case "/school/[[...path]]": return "school";
    case "/lessons/[[...path]]": return "lessons";
    case "/store": case "/store/[id]": case "/store/sell": case "/store/sell/[id]": return "store";
    case "/store/mine": case "/read/[id]": return "shelf";
    case "/extensions": case "/extensions/[id]": case "/extensions/new": case "/extensions/[id]/edit": case "/mods": return "extensions";
    case "/admin/review": return "review";
    case "/admin/site": return "customize";
    case "/community": return query.type === "users" ? "people" : "feed";
    case "/reels": return "reels";
    case "/graph": case "/tune": return "map";
    case "/": case "/r/[rid]": return "score";
    case "/daw": return "song";
    case "/synth": return "synth";
    case "/room": return "room";
    case "/practice": case "/sessions/[id]": return "practice";
    case "/vault": return "library";
    case "/byos": return "byos";
    case "/book": case "/account/books": return "books";
    case "/profile": return query.tab === "trees" ? "trees" : query.tab === "saved" ? "saved" : query.tab === "modules" ? "modules" : "profile";
    case "/library": return "saved";
    case "/modules": case "/modules/[id]": case "/modules/new": return "modules";
    case "/admin/moderation": return "moderation";
    case "/notifications": return "notifications";
    case "/login": return "login";
    case "/signup": return "signup";
    default: return null;
  }
}
